/* eslint-disable no-param-reassign */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import TtsEmphasisIcon from '@src/assets/icons/tts-emphasis.svg';

import BadgeAvatar from '@src/components/BadgeAvatar';
import { IconButton } from '@mui/material';
import {
  AvTimerRounded,
  Forward5Rounded,
  Replay5Rounded,
} from '@mui/icons-material';
import {
  SILENCE_AUDIO_URL,
  TIME_DELAY_TRANSFER_BUTTON,
} from '@src/constants/tts';
import actions from '@src/redux/actions';
import apis from '@src/apis';
import { trackAudioPlayed, deriveModule } from '@src/services/moengage';
import { MOE_SURFACE } from '@src/constants/moengage';
import {
  REQUEST_STATUS,
  VOICE_OWNERSHIP,
  VOICE_PROVIDER,
} from '@src/constants/voice';
import { COLOR } from '@src/styles/color';
import useRequestAnimationStatus from '@src/hooks/useRequestAnimationStatus';
import { delay } from '@src/utils/delay';
import { FAVOURITE_VOICES } from '@src/constants/voiceLibrary';
import { useTranslation } from 'react-i18next';
import { useCanUseEOLVoice } from '@src/hooks/useCanUseEOLVoice';
import { VOICE_CLONE_TYPE } from '@src/constants/voiceCloning';
import { StyledAudioPlayer } from './index.styled';
import SpeedDrawer from './SpeedDrawer';
import NewVoiceDrawerMobile from './NewVoiceDrawer/VoiceDrawer';
import PlayButton from './PlayButton';
import AudioMobile from '../AudioPlayer/AudioMobile';
import BreakTimeDrawer from './BreakTimeDrawer';
import EmphasisDrawer from './EmphasisDrawer';

const REWIND_TIME_IN_SECOND = 5;

const AudioPlayer = ({
  buttonStatus,
  audioRef,
  voice,
  speed,
  enableEditor,
  canUserUseVoiceCloning,
  onEnableEditor,
  handleChangeVoice,
  onCreateSynthesis,
  activeVoiceId,
  onChangeOpenWrongSyntaxDialog,
  onChangeButtonStatus,
  onSelectBreakTime,
  onRemovePreviewAudio,
  onShowDialogUpgradeVoiceCloning,
  onShowDialogUpgradeInstantVoiceCloning,
  showWarningCannotUseVoiceDialog,
  BUTTON_STATUS,
  defaultVoiceOwnership,
  onDeleteCommunityVoice,
  openCommunityVoiceDialog,
  showVoice,
  onDeleteVoiceCode,
  voiceCode,
}) => {
  const { t } = useTranslation();
  const [isErrorPlaying, setIsErrorPlaying] = useState(false);
  const [isDefault, setIsDefault] = useState(true);
  const [isClickTransfer, setIsClickTransfer] = useState(false);

  const {
    synthesisRequest,
    advanceFeature,
    defaultSample,
    voice: requestVoice,
  } = useSelector((state) => state.synthesisRequest);
  const user = useSelector((state) => state.auth.user);
  const { displayStatus } = useRequestAnimationStatus(synthesisRequest);

  const {
    audioLink,
    selectedAudioRequest,
    isPlaying,
    requestPreviewId,
    duration,
    isAudioLoading,
    currentTime,
    isRunStreamAudio,
    finalStreamAudio,
    previewRequestInfo,
  } = useSelector((state) => state.audioPlayer);

  // Converted audio only
  const pendingTrackRef = useRef(null);
  useEffect(() => {
    if (!isPlaying) return;
    let playingRequest = null;
    if (previewRequestInfo?.id) playingRequest = previewRequestInfo;
    else if (
      synthesisRequest?.id &&
      audioLink &&
      audioLink === synthesisRequest.audioLink
    )
      playingRequest = synthesisRequest;
    if (!playingRequest) return;
    const attrs = {
      requestId: playingRequest.id,
      module: deriveModule(playingRequest),
      surface: MOE_SURFACE.PLAYER,
      durationSeconds: playingRequest.seconds,
      voiceCode: playingRequest.voice?.code,
    };
    // TTS requests carry no `seconds` — defer to loadedmetadata for duration
    if (attrs.durationSeconds) {
      pendingTrackRef.current = null;
      trackAudioPlayed(attrs);
    } else {
      pendingTrackRef.current = attrs;
    }
  }, [isPlaying, previewRequestInfo, synthesisRequest, audioLink]);
  const { filterVoice } = useSelector((state) => state.voice);
  const { canUseEOLVoice } = useCanUseEOLVoice();
  const dispatch = useDispatch();

  const [openSpeedDrawer, setOpenSpeedDrawer] = useState(false);
  const [openBreakTimeDrawer, setOpenBreakTimeDrawer] = useState(false);
  const [openEmphasisDrawer, setOpenEmphasisDrawer] = useState(false);
  const [openVoiceDrawer, setOpenVoiceDrawer] = useState(false);
  const [openSearchPanel, setOpenSearchPanel] = useState(false);
  const [newPlayAudioLink, setNewPlayAudioLink] = useState(audioLink);

  const handleOpenSpeedDrawer = () => {
    // Set timeout for action after onblur textarea
    setTimeout(() => {
      if (isPlaying) {
        onChangeOpenWrongSyntaxDialog({
          dialogInfo: {
            name: 'checkCanConfig',
            variant: 'warning',
            title: t('note'),
            description: t('notePauseAudio'),
          },
        });
        return;
      }
      setOpenSpeedDrawer(true);
    }, 50);
  };

  const handleOpenBreakTimeDrawer = () => {
    // Set timeout for action after onblur textarea
    setTimeout(() => {
      if (isPlaying) {
        onChangeOpenWrongSyntaxDialog({
          dialogInfo: {
            name: 'checkCanConfig',
            variant: 'warning',
            title: t('note'),
            description: t('notePauseAudio'),
          },
        });
        return;
      }
      setOpenBreakTimeDrawer(true);
    }, 50);
  };

  const handleCloseSpeedDrawer = () => setOpenSpeedDrawer(false);
  const handleCloseBreakTimeDrawer = () => setOpenBreakTimeDrawer(false);
  const handleCloseEmphasisDrawer = () => setOpenEmphasisDrawer(false);

  const handleOpenEmphasisDrawer = () => {
    // Set timeout for action after onblur textarea
    setTimeout(() => {
      if (isPlaying) {
        onChangeOpenWrongSyntaxDialog({
          dialogInfo: {
            name: 'checkCanConfig',
            variant: 'warning',
            title: t('note'),
            description: t('notePauseAudio'),
          },
        });
        return;
      }
      setOpenEmphasisDrawer(true);
    }, 50);
  };

  const handleOpenVoiceDrawer = () => {
    setTimeout(() => {
      if (isPlaying) {
        onChangeOpenWrongSyntaxDialog({
          dialogInfo: {
            name: 'checkCanConfig',
            variant: 'warning',
            title: t('note'),
            description: t('notePauseAudio'),
          },
        });
        return;
      }
      // TODO: Remove/Update this below logic, only update if voiceOwnershipFilter is not found
      const voiceOwnershipFilter = filterVoice?.voiceOwnership;
      const ownershipOfUsingVoice = requestVoice?.provider;
      let voiceOwnership = VOICE_OWNERSHIP.VBEE;
      if (
        !Object.values(VOICE_OWNERSHIP).includes(voiceOwnershipFilter) &&
        requestVoice
      ) {
        voiceOwnership = FAVOURITE_VOICES;
      }
      voiceOwnership =
        ownershipOfUsingVoice === VOICE_PROVIDER.VBEE_VOICE_CLONING
          ? VOICE_OWNERSHIP.PERSONAL
          : VOICE_OWNERSHIP.VBEE;

      if (
        ownershipOfUsingVoice === VOICE_PROVIDER.VBEE_VOICE_CLONING &&
        (voiceOwnershipFilter === VOICE_OWNERSHIP.COMMUNITY ||
          requestVoice?.userId !== user?.id)
      )
        voiceOwnership = VOICE_OWNERSHIP.COMMUNITY;

      if (!voiceOwnershipFilter || voiceOwnershipFilter !== voiceOwnership)
        dispatch(
          actions.voice.changeFilterVoice({
            ...filterVoice,
            voiceOwnership,
          }),
        );

      setOpenVoiceDrawer(true);
    }, 50);
  };

  useEffect(() => {
    const isOpenVoices = showVoice && voiceCode && requestVoice.code;
    if (isOpenVoices) {
      handleOpenVoiceDrawer();
    }
  }, [showVoice, voiceCode, requestVoice.code]);

  const handleCloseVoiceDrawer = () => {
    setOpenSearchPanel(false);
    setOpenVoiceDrawer(false);
  };
  const [audioEnd, setAudioEnd] = useState(false);

  const handleLoadedMetadata = () => {
    const audioDuration = audioRef.current.duration;
    dispatch(actions.audioPlayer.updateMetaData({ duration: audioDuration }));
    const src = audioRef.current.src ? new URL(audioRef.current.src) : null;
    if (src?.pathname === SILENCE_AUDIO_URL) return;
    if (pendingTrackRef.current) {
      trackAudioPlayed({
        ...pendingTrackRef.current,
        durationSeconds: Number.isFinite(audioDuration)
          ? Math.round(audioDuration)
          : undefined,
      });
      pendingTrackRef.current = null;
    }
  };

  const handleAudioTimeUpdate = () => {
    const url = audioRef.current.src ? new URL(audioRef.current.src) : '';
    const time =
      url?.pathname === SILENCE_AUDIO_URL ? 0 : audioRef.current.currentTime;
    dispatch(actions.audioPlayer.updateMetaData({ currentTime: time }));
  };

  const handleAudioEnd = () => {
    if (audioEnd) {
      audioRef.current.pause();
      dispatch(actions.audioPlayer.updateStatus(false));
      dispatch(actions.audioPlayer.updateMetaData({ currentTime: 0 }));
      setAudioEnd(false);
      return;
    }

    if (newPlayAudioLink) {
      setAudioEnd(true);
      audioRef.current.src = newPlayAudioLink;
      if (!finalStreamAudio && isRunStreamAudio) {
        audioRef.current.load();
        audioRef.current.play();
        return;
      }
      audioRef.current.pause();
      dispatch(actions.audioPlayer.updateStatus(false));
      dispatch(actions.audioPlayer.updateMetaData({ currentTime: 0 }));
      return;
    }

    audioRef.current.src = SILENCE_AUDIO_URL;
    audioRef.current.load();
    audioRef.current.play();
  };

  const handleSeeked = (time) => {
    audioRef.current.currentTime = time;
    dispatch(actions.audioPlayer.updateMetaData({ currentTime: time }));
  };

  const handleGetPresignedAudioUrl = async (keyId) => {
    try {
      const res = await apis.requests.getPresignedAudioUrl(keyId);
      if (res?.status) {
        const audioUrl = res.result?.audio;
        setNewPlayAudioLink(audioUrl);
        return;
      }
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'downloadError',
        }),
      );
      setNewPlayAudioLink(audioLink);
      return;
    } catch (error) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'downloadError',
        }),
      );
      setNewPlayAudioLink(audioLink);
    }
  };

  const handlePlayAudio = (sampleAudioLink) => {
    const newAudioLink = sampleAudioLink || synthesisRequest.audioLink;
    if (newPlayAudioLink === newAudioLink) {
      dispatch(actions.audioPlayer.updateStatus(!isPlaying));
    } else {
      // eslint-disable-next-line no-param-reassign
      audioRef.current.src = SILENCE_AUDIO_URL;
      audioRef.current.load();
      audioRef.current.play();
      dispatch(actions.audioPlayer.updateAudioLink(newAudioLink));
      dispatch(actions.audioPlayer.updateStatus(true));
      dispatch(actions.audioPlayer.updateMetaData({ currentTime: 0 }));
      dispatch(actions.audioPlayer.updateShowAudioPlayer(true));
    }
  };

  const handleCheckIsSampleScript = () => {
    const { text: sampleText, audios } = defaultSample;
    const defaultSampleData = audios?.[0] || {};
    const {
      audioType: sampleAudioType,
      bitrate: sampleBitrate,
      sampleRate: sampleSampleRate,
      voiceCode: sampleVoiceCode,
      audioLink: sampleAudioLink,
      speed: sampleSpeed,
    } = defaultSampleData;

    const { audioType, bitrate, sampleRate, text } = synthesisRequest;

    const isDifferent = (value, sampleValue) =>
      value !== undefined && value !== sampleValue;

    if (
      isDifferent(text, sampleText) ||
      isDifferent(audioType, sampleAudioType) ||
      isDifferent(bitrate, sampleBitrate) ||
      isDifferent(sampleRate, sampleSampleRate) ||
      isDifferent(voice?.code, sampleVoiceCode) ||
      isDifferent(speed, sampleSpeed)
    )
      return;

    const dataSynthesis = {
      text: sampleText,
      title: synthesisRequest.title,
      audioLink: sampleAudioLink,
      speed: sampleSpeed,
      voice,
    };

    handlePlayAudio(sampleAudioLink);
    dispatch(actions.synthesisRequest.updateSynthesisConfig(dataSynthesis));
    dispatch(actions.synthesisRequest.updateLoadingSynthesisConfig(true));
    dispatch(actions.audioPlayer.updatePreviewRequestInfo(dataSynthesis));
    dispatch(actions.synthesisRequest.updateShowSampleScripts(false));
  };

  const handleChangePlayAudio = () => {
    dispatch(actions.audioPlayer.updateStatus(!isPlaying));
    setAudioEnd(true);
  };

  const handleClickTransfer = async () => {
    setTimeout(async () => {
      if (!isClickTransfer) {
        setIsClickTransfer(true);
        await onCreateSynthesis(isDefault);
        await delay(TIME_DELAY_TRANSFER_BUTTON);
        setIsClickTransfer(false);
        handleCheckIsSampleScript();
      }
    }, 50);
  };

  const handleClickPlayButton = () => {
    switch (buttonStatus) {
      case BUTTON_STATUS.CREATE: {
        const eolVoice = voice?.eolDate;
        if (eolVoice && !canUseEOLVoice({ voice, user })) {
          showWarningCannotUseVoiceDialog();
          break;
        }

        if (
          requestVoice?.provider === VOICE_PROVIDER.VBEE_VOICE_CLONING &&
          requestVoice?.userId === user?.id &&
          !canUserUseVoiceCloning
        ) {
          if (voice?.type === VOICE_CLONE_TYPE.ZERO_SHOT)
            onShowDialogUpgradeInstantVoiceCloning();
          else onShowDialogUpgradeVoiceCloning();
        } else handleClickTransfer();
        break;
      }

      case BUTTON_STATUS.PLAYING: {
        handleChangePlayAudio();
        break;
      }
      case BUTTON_STATUS.LOADING:
      case BUTTON_STATUS.PAUSE:
        break;

      default:
        break;
    }
  };

  const updateRequestStatus = () => {
    if (!enableEditor) {
      onEnableEditor(true);
      dispatch(actions.synthesisRequest.cloneSynthesisRequest());
    }
  };

  const checkDefaultState = () => {
    const { text: sampleText, audios } = defaultSample;
    const defaultSampleData = audios?.[0] || {};
    const { voiceCode: sampleVoiceCode, speed: sampleSpeed } =
      defaultSampleData;

    const { text } = synthesisRequest;

    const isDifferent = (value, sampleValue) =>
      sampleValue !== undefined && value !== undefined && value !== sampleValue;

    if (
      synthesisRequest.text?.length ||
      isDifferent(text, sampleText) ||
      isDifferent(voice?.code, sampleVoiceCode) ||
      isDifferent(speed, sampleSpeed)
    ) {
      setIsDefault(false);
    } else setIsDefault(true);
  };

  const handleChangeSynthesisRequest = (name, value) => {
    dispatch(actions.synthesisRequest.updateSynthesisRequest(name, value));
    dispatch(actions.synthesisRequest.updateSynthesisRequest('status', ''));
    updateRequestStatus();
    onRemovePreviewAudio();
  };

  const handleChangeSpeed = (name, value) => {
    handleChangeSynthesisRequest(name, value);
    updateRequestStatus();
    onRemovePreviewAudio();
  };

  const handleChangeEmphasis = (name, value) => {
    handleChangeSynthesisRequest(name, value);
    updateRequestStatus();
    onRemovePreviewAudio();
  };

  useEffect(() => {
    if (selectedAudioRequest) {
      handleGetPresignedAudioUrl(selectedAudioRequest);
    } else {
      setNewPlayAudioLink(audioLink);
    }
  }, [audioLink]);

  useEffect(() => {
    const { createdAt, retentionPeriod = 3 } = synthesisRequest;
    const audioExpired = moment().isAfter(
      moment(createdAt).add(retentionPeriod, 'days'),
    );

    if (!synthesisRequest.status || (createdAt && audioExpired)) {
      onChangeButtonStatus(BUTTON_STATUS.CREATE);
      dispatch(
        actions.audioPlayer.updateMetaData({ currentTime: 0, duration: 0 }),
      );
    }

    if (synthesisRequest.status === REQUEST_STATUS.IN_PROGRESS)
      onChangeButtonStatus(BUTTON_STATUS.LOADING);
    else if (
      synthesisRequest.status === REQUEST_STATUS.SUCCESS &&
      displayStatus === REQUEST_STATUS.SUCCESS &&
      !audioExpired
    ) {
      onChangeButtonStatus(BUTTON_STATUS.PLAYING);
      handlePlayAudio();
    }
  }, [synthesisRequest.processingAt, synthesisRequest.status, displayStatus]);

  useEffect(() => {
    if (isPlaying && !requestPreviewId)
      onChangeButtonStatus(BUTTON_STATUS.PLAYING);
  }, [isPlaying, requestPreviewId]);

  useEffect(() => {
    checkDefaultState();
  }, [defaultSample, synthesisRequest, voice]);

  useEffect(() => {
    audioRef.current.load();
    audioRef.current.currentTime = currentTime;

    if (isPlaying) {
      audioRef.current.play().catch((error) => {
        // eslint-disable-next-line no-console
        console.log(`Error when play audio ${error}`);
        const ERROR_DUE_TO_EXPIRED_AUDIO_LINK = 'NotSupportedError';
        if (error.name === ERROR_DUE_TO_EXPIRED_AUDIO_LINK) {
          handleGetPresignedAudioUrl(selectedAudioRequest);
        }
      });
    } else {
      audioRef.current.pause();
    }
  }, [isPlaying]);

  useEffect(() => {
    audioRef.current.load();
    audioRef.current.currentTime = currentTime;

    if ((newPlayAudioLink && isPlaying) || isRunStreamAudio) {
      audioRef.current.play().catch((error) => {
        // eslint-disable-next-line no-console
        console.log(`Error when play audio ${error}`);
        const ERROR_DUE_TO_EXPIRED_AUDIO_LINK = 'NotSupportedError';
        if (error.name === ERROR_DUE_TO_EXPIRED_AUDIO_LINK) {
          setIsErrorPlaying(true);
          handleGetPresignedAudioUrl(selectedAudioRequest);
        }
      });
    } else {
      audioRef.current.pause();
    }

    if (isPlaying && isAudioLoading) {
      dispatch(
        actions.audioPlayer.updateTryListeningSentence({
          sentenceId: '',
          isAudioLoading: false,
        }),
      );
    }
  }, [newPlayAudioLink, isPlaying, isRunStreamAudio]);

  useEffect(() => {
    if (newPlayAudioLink) {
      audioRef.current.src = newPlayAudioLink;
      if (isErrorPlaying) {
        audioRef.current.play();
        setIsErrorPlaying(false);
      }
      dispatch(actions.audioPlayer.updateStatusStreamAudio(false));
    }
    setAudioEnd(false);
  }, [newPlayAudioLink]);

  useEffect(() => {
    if (selectedAudioRequest) {
      handleGetPresignedAudioUrl(selectedAudioRequest);
    } else {
      setNewPlayAudioLink(audioLink);
    }
  }, [audioLink]);

  useEffect(() => {
    if (defaultVoiceOwnership && openCommunityVoiceDialog) {
      setOpenVoiceDrawer(true);
    }
  }, []);

  return (
    <StyledAudioPlayer>
      <AudioMobile
        isPlaying={isPlaying}
        currentTime={currentTime}
        duration={duration}
        onSeeked={handleSeeked}
        disabled={!newPlayAudioLink}
        hiddenDuration
        color={COLOR.darkBlue}
        hightThumb="12px"
        widthThumb="12px"
      />
      <div className="audio-info">
        <div className="audio-info-left-group">
          <div
            className="voice"
            tabIndex="0"
            role="button"
            onClick={handleOpenVoiceDrawer}
          >
            <BadgeAvatar
              width={32}
              smallImgWidth={10}
              img={voice && voice.roundImage}
              smallImg={voice && voice.language && voice.language.roundImage}
              type="image"
            />
          </div>

          <IconButton
            className="break-time-button"
            disabled={voice?.provider !== VOICE_PROVIDER.VBEE}
            onClick={handleOpenBreakTimeDrawer}
          >
            <AvTimerRounded />
          </IconButton>
        </div>
        <div className="control-audio">
          <div>
            <IconButton
              onClick={() => {
                const time = currentTime - REWIND_TIME_IN_SECOND;
                handleSeeked(time >= 0 ? time : 0);
              }}
              className="rewind-button"
              disabled={displayStatus === REQUEST_STATUS.IN_PROGRESS}
              color="iconPrimary"
            >
              <Replay5Rounded sx={{ fontSize: 26 }} />
            </IconButton>
          </div>

          <PlayButton
            buttonStatus={buttonStatus}
            request={synthesisRequest}
            isPlaying={isPlaying}
            onClickPlayButton={handleClickPlayButton}
          />

          <div>
            <IconButton
              onClick={() => handleSeeked(currentTime + REWIND_TIME_IN_SECOND)}
              disabled={
                currentTime > duration ||
                displayStatus === REQUEST_STATUS.IN_PROGRESS
              }
              className="rewind-button"
              color="iconPrimary"
            >
              <Forward5Rounded sx={{ fontSize: 26 }} />
            </IconButton>
          </div>
        </div>

        <div className="audio-info-right-group">
          {synthesisRequest.hasEmphasis ? (
            <IconButton onClick={handleOpenEmphasisDrawer}>
              <div className="emphasis-button">
                <img src={TtsEmphasisIcon} alt="emphasis" />
              </div>
            </IconButton>
          ) : (
            <div />
          )}
          <div
            onClick={handleOpenSpeedDrawer}
            className="speed"
            role="button"
            tabIndex={0}
          >
            {speed}x
          </div>
        </div>
      </div>
      <SpeedDrawer
        open={openSpeedDrawer}
        onOpen={handleOpenSpeedDrawer}
        onClose={handleCloseSpeedDrawer}
        onChange={
          advanceFeature ? handleChangeSpeed : handleChangeSynthesisRequest
        }
        speedValue={synthesisRequest?.speed}
      />
      <BreakTimeDrawer
        open={openBreakTimeDrawer}
        onClose={handleCloseBreakTimeDrawer}
        onChange={onSelectBreakTime}
      />
      {openVoiceDrawer && (
        <NewVoiceDrawerMobile
          open={openVoiceDrawer}
          canUserUseVoiceCloning={canUserUseVoiceCloning}
          onOpen={handleOpenVoiceDrawer}
          onClose={handleCloseVoiceDrawer}
          onChangeVoice={handleChangeVoice}
          openSearchPanel={openSearchPanel}
          setOpenSearchPanel={setOpenSearchPanel}
          activeVoiceId={activeVoiceId}
          onChangeOpenWrongSyntaxDialog={onChangeOpenWrongSyntaxDialog}
          onShowDialogUpgradeVoiceCloning={onShowDialogUpgradeVoiceCloning}
          onShowDialogUpgradeInstantVoiceCloning={
            onShowDialogUpgradeInstantVoiceCloning
          }
          defaultVoiceOwnership={defaultVoiceOwnership}
          onDeleteCommunityVoice={onDeleteCommunityVoice}
          voiceCode={voiceCode}
          onDeleteVoiceCode={onDeleteVoiceCode}
        />
      )}
      <EmphasisDrawer
        open={openEmphasisDrawer}
        onClose={handleCloseEmphasisDrawer}
        currentEmphasis={synthesisRequest.emphasis}
        onChange={handleChangeEmphasis}
      />

      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio
        ref={audioRef}
        autoPlay={isPlaying}
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleAudioTimeUpdate}
        onEnded={handleAudioEnd}
      >
        <source src={newPlayAudioLink} type="audio/ogg" />
      </audio>
    </StyledAudioPlayer>
  );
};

export default AudioPlayer;
